<script setup lang="ts">
    import { computed } from 'vue';
    import { useOverlayStore } from '@/views/dicoms/overlay/overlayStore';
    import OverlayMainView from '@/views/dicoms/overlay/OverlayMainView.vue';
    import OverlaySideView from '@/views/dicoms/overlay/OverlaySideView.vue';
    import SurgicalGuideDownloads from '@/views/dicoms/overlay/SurgicalGuideDownloads.vue';
    import { useSurgicalGuideOrders } from '@/stores/surgicalGuideOrders/store';
    import { useSurgicalGuideCase } from '@/stores/surgicalGuide/caseStore';
    import { useRoute } from 'vue-router';
    import { caseIdFromRoute } from '@/router';
    import { type CatstackPlaneId, catstackPlaneIds } from '@/domain/anatomy/catstackPlane';

    const overlayStore = useOverlayStore();
    const ordersStore = useSurgicalGuideOrders();
    const route = useRoute();

    const caseId = caseIdFromRoute(route);

    await overlayStore.loadCase(caseId);
    await ordersStore.load(caseId);
    await useSurgicalGuideCase().loadExistingCase(caseId);

    const allViews: CatstackPlaneId[] = [...catstackPlaneIds];

    const selectedView = computed(() => overlayStore.catstackMode);

    const currentSliceMax = computed(() => {
        return overlayStore.catstacksData && overlayStore.catstacksData[selectedView.value]
            ? overlayStore.catstacksData[selectedView.value].catstack.count - 1
            : 0;
    });

    const currentSliceValue = computed({
        get: () => {
            return (
                currentSliceMax.value -
                (overlayStore.catstacks[selectedView.value]?.sliceIndex || 0)
            );
        },
        set: (value) => {
            if (overlayStore.catstacks[selectedView.value]) {
                overlayStore.catstacks[selectedView.value].sliceIndex =
                    currentSliceMax.value - value;
            }
        },
    });
</script>

<template>
    <div class="surgical-guide-container">
        <div class="overlay-layout-container">
            <div v-if="!overlayStore.isLoading" class="overlay-layout">
                <div class="main-view-container">
                    <OverlayMainView />
                </div>

                <div class="side-views">
                    <OverlaySideView
                        v-for="view in allViews"
                        :key="view"
                        :view-type="view"
                        :is-selected="selectedView === view"
                    />
                </div>
            </div>

            <div v-if="!overlayStore.isLoading" class="overlay-controls">
                <div class="range-holder">
                    <label class="control-label">
                        {{ selectedView.charAt(0).toUpperCase() + selectedView.slice(1) }} Slice:
                    </label>
                    <v-slider
                        v-model="currentSliceValue"
                        :min="0"
                        :max="currentSliceMax"
                        step="1"
                        color="primary"
                        track-size="2.5"
                        track-color="grey-light-1"
                        thumb-label
                        thumb-size="12"
                        hide-details
                    />
                </div>
            </div>
        </div>
        <div class="pl-5">
            <SurgicalGuideDownloads :case-id="caseId" />
        </div>
    </div>
</template>

<style scoped lang="scss">
    .surgical-guide-container {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
    }

    .overlay-layout-container {
        display: flex;
        flex-direction: column;
        background-color: #222;
        position: relative;
    }

    .overlay-layout {
        flex: 1;
        display: flex;
        gap: 20px;
        padding: 20px;
        min-height: 500px;
    }

    .main-view-container {
        flex: 3;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .side-views {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 10px;
        height: 100%;
    }

    .overlay-controls {
        background-color: #2c2f33;
        padding: 20px;
        border-top: 1px solid #444;
    }

    .range-holder {
        display: flex;
        align-items: center;
        gap: 20px;
        max-width: 500px;
    }

    .control-label {
        color: #fff;
        font-size: 14px;
        font-weight: 500;
        min-width: 120px;
    }

    :deep(.v-slider) {
        .v-slider__thumb {
            background-color: rgb(var(--v-theme-primary));
        }

        .v-slider__track-fill {
            background-color: rgb(var(--v-theme-primary));
        }
    }
</style>
